/**
 * Determines whether a color is perceived as light or dark based on its perceived brightness.
 *
 * @param color - The color string in hexadecimal (#FFFFFF, #FFF) or RGB/RGBA format (rgb(255, 255, 255), rgba(255, 255, 255, 1))
 * @returns True if the color is perceived as light, false if it's perceived as dark
 *
 * @example
 * ```typescript
 * isLightColor("#FFFFFF"); // returns true (white is light)
 * isLightColor("#000000"); // returns false (black is dark)
 * isLightColor("rgb(255, 0, 0)"); // returns true depending on perceived brightness
 * ```
 */
export const isLightColor = (color: string): boolean => {
	try {
		let r, g, b;

		// Check if the color is a valid string
		if (!color || typeof color !== "string" || color.trim() === "") {
			// console.warn("isLightColor: Input color string is empty or not a string.");
			return true; // Default for invalid input type or empty string
		}

		const sanitizedColor = color.trim().toLowerCase(); // Standardize input

		// Check the format of the color, HEX or RGB?
		if (sanitizedColor.match(/^rgb/)) {
			// If HEX --> store the red, green, blue values in separate variables
			const rgb = sanitizedColor.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

			// Handle the case where regex match might be null
			if (rgb) {
				r = parseInt(rgb[1] || "0", 10);
				g = parseInt(rgb[2] || "0", 10);
				b = parseInt(rgb[3] || "0", 10);
			} else {
				// Default to white if RGB parsing fails
				r = g = b = 255;
			}
		} else {
			// If RGB --> Convert it to HEX: http://gist.github.com/983661
			const hexColor = +("0x" + sanitizedColor.slice(1).replace(sanitizedColor.length < 5 && /./g, "$&$&"));

			r = hexColor >> 16;
			g = (hexColor >> 8) & 255;
			b = hexColor & 255;
		}

		// HSP equation from http://alienryderflex.com/hsp.html
		// This formula calculates the perceived brightness of a color.
		const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

		// Using the HSP value, determine whether the color is light or dark
		// A common threshold is 127.5 on a scale of 0-255.
		// Values above 127.5 are considered light, below are dark.
		return hsp > 127.5;
	} catch (ex) {
		//console.error("Error in isLightColor: ", ex);
		return true;
	}
};
